import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import api from '@/api/client'

/**
 * Settings → Agent access.
 *
 * Two things live here (Phase 2 of docs/ssh_agent_console_support_plan.md):
 *
 *  1. **Access tokens** — the credential that lets a user run `claude` / `codex`
 *     themselves (over SSH on the server, or on their own laptop) with the full
 *     Twasta tool set. The plaintext is shown ONCE at mint: only its sha256 is
 *     stored, so it is unrecoverable afterwards by anyone, including admins.
 *
 *  2. **Pending device logins** — when the user runs `twasta login` on another
 *     machine, that machine prints a short code. Approving it here is what
 *     attaches an identity to it; the token is minted for the approver and
 *     delivered to the waiting CLI, never shown in this browser.
 */

interface TokenInfo {
  id: string
  name: string | null
  prefix: string
  scope_kind: string
  project_ids: string | null
  surface: string
  created_at: string | null
  expires_at: string | null
  last_used_at: string | null
  last_used_ip: string | null
  use_count: number
  revoked: boolean
}

const SURFACES = [
  { value: 'any', label: 'Any (SSH or laptop)' },
  { value: 'ssh', label: 'Server SSH console only' },
  { value: 'remote', label: 'Own machine only' },
  { value: 'askai', label: 'Ask-AI runs only (Claude Code / scripts driving Ask-AI)' },
]

const EXPIRY_CHOICES = [
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
  { value: '', label: 'Never expires' },
]

function fmt(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString()
}

const card: React.CSSProperties = {
  background: 'var(--color-bg-secondary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: '1.5rem',
  marginBottom: '1.5rem',
}

const label: React.CSSProperties = {
  display: 'block',
  fontSize: '0.875rem',
  fontWeight: 500,
  marginBottom: 4,
  color: 'var(--color-text-secondary)',
}

const input: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.625rem',
  fontSize: '0.9375rem',
  background: 'var(--color-bg-primary)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
}

export default function AgentAccessPanel() {
  const [tokens, setTokens] = useState<TokenInfo[]>([])
  const [loading, setLoading] = useState(true)

  // Mint form
  const [name, setName] = useState('')
  const [surface, setSurface] = useState('any')
  const [expiry, setExpiry] = useState('90')
  const [scopeProjects, setScopeProjects] = useState('')
  const [minting, setMinting] = useState(false)

  // The one and only time the plaintext exists in this browser.
  const [freshToken, setFreshToken] = useState<string | null>(null)

  // Device-code approval
  const [userCode, setUserCode] = useState('')
  const [pending, setPending] = useState<{
    user_code: string; project: string | null; surface: string
    client_info: string | null; expires_at: string | null
  } | null>(null)
  const [checking, setChecking] = useState(false)

  // The installer command, addressed to THIS server. Taken from the browser's
  // own origin rather than configured anywhere: whatever address the user
  // reached Twasta on is by definition one their machine can resolve.
  //
  // Windows gets a DIFFERENT command, not a footnote: `curl … | sh` does not
  // run in PowerShell, and "install WSL first" is where a Windows user stops.
  const origin = window.location.origin
  const isWindows = /win/i.test(
    (navigator as any).userAgentData?.platform || navigator.platform ||
    navigator.userAgent)
  const posixCmd = `curl -fsSL ${origin}/api/agent-access/install | sh`
  const windowsCmd = `irm ${origin}/api/agent-access/install.ps1 | iex`
  const installCmd = isWindows ? windowsCmd : posixCmd

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied')
    } catch {
      // Clipboard access is blocked on plain http:// origins and in some
      // hardened browsers — the command is visible and selectable regardless.
      toast.error('Could not copy — select the text and copy it manually')
    }
  }

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/agent-access/tokens')
      setTokens(data.tokens || [])
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not load agent tokens')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const mint = async () => {
    setMinting(true)
    try {
      const projects = scopeProjects
        .split(',').map((s) => s.trim()).filter(Boolean)
      const { data } = await api.post('/agent-access/tokens', {
        name: name.trim(),
        scope_kind: projects.length ? 'projects' : 'tenant',
        project_ids: projects.length ? projects : null,
        surface,
        expires_in_days: expiry === '' ? null : Number(expiry),
      })
      setFreshToken(data.token)
      setName('')
      setScopeProjects('')
      await load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not create the token')
    } finally {
      setMinting(false)
    }
  }

  const revoke = async (token: TokenInfo) => {
    // Revocation takes effect on the agent's NEXT call, not at its next login —
    // worth saying, because "is it dead yet?" is the first thing you ask.
    if (!window.confirm(
      `Revoke "${token.name || token.prefix}"?\n\nAny agent session using it ` +
      `stops working on its next call.`)) return
    try {
      await api.delete(`/agent-access/tokens/${token.id}`)
      toast.success('Token revoked')
      await load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not revoke the token')
    }
  }

  const lookupCode = async () => {
    const code = userCode.trim().toUpperCase()
    if (!code) return
    setChecking(true)
    setPending(null)
    try {
      const { data } = await api.get('/agent-access/device/pending', {
        params: { user_code: code },
      })
      setPending(data)
    } catch (err: any) {
      toast.error(err?.response?.data?.detail
        || 'That code is unknown, already used or expired')
    } finally {
      setChecking(false)
    }
  }

  // A sign-in link may land HERE rather than on /agent-access — an older
  // backend still points at this tab. Prefill and look the code up so the
  // person is never asked to retype what the link already carried.
  const [urlParams] = useSearchParams()
  const autoLookedUp = useRef(false)
  useEffect(() => {
    const code = (urlParams.get('code') || '').trim().toUpperCase()
    if (!code || autoLookedUp.current) return
    autoLookedUp.current = true
    setUserCode(code)
    void (async () => {
      setChecking(true)
      try {
        const { data } = await api.get('/agent-access/device/pending', {
          params: { user_code: code },
        })
        setPending(data)
      } catch {
        // Silent: an expired or already-used code from a stale link should not
        // greet someone opening Settings with a red toast.
      } finally {
        setChecking(false)
      }
    })()
  }, [urlParams])

  const approve = async () => {
    if (!pending) return
    try {
      await api.post('/agent-access/device/approve', {
        user_code: pending.user_code,
        name: pending.client_info || 'device login',
      })
      toast.success('Approved — the other machine will pick up its token shortly')
      setPending(null)
      setUserCode('')
      await load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not approve')
    }
  }

  const deny = async () => {
    if (!pending) return
    try {
      await api.post('/agent-access/device/deny', { user_code: pending.user_code })
      toast.success('Request denied')
      setPending(null)
      setUserCode('')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not deny')
    }
  }

  return (
    <>
      {/* ---- the one-time token reveal ---- */}
      {freshToken && (
        <section style={{
          ...card,
          borderColor: 'var(--color-accent)',
          background: 'var(--color-bg-primary)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>
              vpn_key
            </span>
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 600 }}>
              Copy your token now
            </h3>
          </div>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            This is the only time it can be shown. Twasta stores just a hash of
            it, so it cannot be retrieved again — by you or by an administrator.
            If you lose it, revoke it and mint a new one.
          </p>
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            padding: '0.625rem 0.75rem',
          }}>
            <code style={{
              flex: 1, fontSize: '0.875rem', fontFamily: 'var(--font-mono, monospace)',
              wordBreak: 'break-all',
            }}>{freshToken}</code>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(freshToken)
                toast.success('Copied')
              }}
              style={{
                padding: '0.375rem 0.75rem', fontSize: '0.875rem',
                background: 'var(--color-accent)', color: '#fff',
                border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >Copy</button>
          </div>
          <div style={{ marginTop: 12 }}>
            <button
              onClick={() => setFreshToken(null)}
              style={{
                padding: '0.375rem 0.75rem', fontSize: '0.875rem',
                background: 'transparent', color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', cursor: 'pointer',
              }}
            >I've copied it — hide</button>
          </div>
        </section>
      )}

      {/* ---- what this is for ---- */}
      <section style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <span className="material-icons" style={{ fontSize: 21, color: 'var(--color-accent)' }}>
            terminal
          </span>
          <h2 style={{ fontSize: '1.125rem', fontWeight: 600 }}>Agent access</h2>
        </div>
        <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
          Run the coding agent yourself — on your own machine, or over SSH on the
          Twasta server — with the same tools, skills and specialist sub-agents
          the in-app Ask&nbsp;AI uses. You use your OWN Claude/Codex subscription
          there, and your work shows up in Twasta as normal.
          <br /><br />
          A token can only reach projects you already have access to; it never
          grants anything extra. Revoking one stops the agent on its next call.
        </p>
      </section>

      {/* ---- laptop setup ----
          The two commands, with THIS server's address already filled in. Users
          arrive here from `twasta login`'s printed URL, but they also arrive
          here first, looking for how to start — and telling them to "run the
          installer" without the address is the step everyone gets wrong. */}
      <section style={card}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>
          Set up your own machine
        </h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          You need a coding agent (<code>claude</code>, <code>codex</code> or{' '}
          <code>agy</code>) and Python 3. Paste these into{' '}
          {isWindows ? 'PowerShell' : 'a terminal'} on your laptop — no token
          needed, the second command signs you in and starts a new application.
        </p>
        {[installCmd, 'twasta new'].map((cmd, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
          }}>
            <code style={{
              flex: 1, minWidth: 0, padding: '0.5rem 0.625rem',
              background: 'var(--color-bg-primary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem', overflowX: 'auto', whiteSpace: 'pre',
            }}>{cmd}</code>
            <button
              onClick={() => void copy(cmd)}
              style={{
                padding: '0.4375rem 0.75rem', fontSize: '0.875rem',
                background: 'transparent', color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', cursor: 'pointer',
                flexShrink: 0,
              }}
            >Copy</button>
          </div>
        ))}
        <p style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          marginTop: 10, lineHeight: 1.6,
        }}>
          <code>twasta new</code> prints a code — enter it below to approve it.
          Then <code>cd</code> into the folder it created and run{' '}
          <code>twasta run</code>, and describe the application you want. Your
          edits sync back here automatically.
          <br />
          Already have a project? Use <code>twasta mount</code> instead.
          {' '}The installer puts <code>twasta</code> on your PATH — on macOS and
          Linux that takes effect in new terminal windows, so the one you just
          used needs <code>~/.twasta/bin/twasta</code> (it prints this too).
          <br />
          On {isWindows ? 'macOS or Linux' : 'Windows'}, the first command is:{' '}
          <code>{isWindows ? posixCmd : windowsCmd}</code>
        </p>
      </section>

      {/* ---- pending device login ---- */}
      <section style={card}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 6 }}>
          Approve a sign-in from another machine
        </h3>
        <p style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)', marginBottom: 12 }}>
          Ran <code>twasta new</code> or <code>twasta mount</code> elsewhere?
          The link it printed opens the approval screen directly — this box is
          here for when you would rather type the code than follow the link.
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <label style={label}>Code</label>
            <input
              style={{ ...input, letterSpacing: '0.08em', textTransform: 'uppercase' }}
              value={userCode}
              placeholder="XXXX-XXXX"
              onChange={(e) => setUserCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void lookupCode() }}
            />
          </div>
          <button
            onClick={() => void lookupCode()}
            disabled={checking || !userCode.trim()}
            style={{
              padding: '0.5rem 1rem', fontSize: '0.9375rem',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              cursor: checking || !userCode.trim() ? 'not-allowed' : 'pointer',
              opacity: checking || !userCode.trim() ? 0.6 : 1,
            }}
          >{checking ? 'Checking…' : 'Look up'}</button>
        </div>

        {pending && (
          <div style={{
            marginTop: 14, padding: '0.875rem',
            background: 'var(--color-bg-primary)',
            border: '1px solid var(--color-accent)',
            borderRadius: 'var(--radius-md)',
          }}>
            <div style={{ fontSize: '0.875rem', lineHeight: 1.8 }}>
              <div><strong>Machine:</strong> {pending.client_info || 'unknown'}</div>
              <div><strong>Project requested:</strong> {pending.project || 'any'}</div>
              <div><strong>Surface:</strong> {pending.surface}</div>
              <div><strong>Expires:</strong> {fmt(pending.expires_at)}</div>
            </div>
            <p style={{
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
              margin: '10px 0',
            }}>
              Approving mints a token in YOUR name. Only approve if you started
              this sign-in.
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                onClick={() => void approve()}
                style={{
                  padding: '0.4375rem 0.875rem', fontSize: '0.875rem',
                  background: 'var(--color-accent)', color: '#fff',
                  border: 'none', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                }}
              >Approve</button>
              <button
                onClick={() => void deny()}
                style={{
                  padding: '0.4375rem 0.875rem', fontSize: '0.875rem',
                  background: 'transparent', color: 'var(--color-danger, #d33)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)', cursor: 'pointer',
                }}
              >Deny</button>
            </div>
          </div>
        )}
      </section>

      {/* ---- mint ---- */}
      <section style={card}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>
          Create a token
        </h3>
        <div style={{
          display: 'grid', gap: '0.875rem',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        }}>
          <div>
            <label style={label}>Label</label>
            <input style={input} value={name} placeholder="my laptop"
                   onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label style={label}>Usable from</label>
            <select style={input} value={surface}
                    onChange={(e) => setSurface(e.target.value)}>
              {SURFACES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Expires</label>
            <select style={input} value={expiry}
                    onChange={(e) => setExpiry(e.target.value)}>
              {EXPIRY_CHOICES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={label}>Limit to projects (optional)</label>
            <input style={input} value={scopeProjects}
                   placeholder="Sales_App, HR_Portal"
                   onChange={(e) => setScopeProjects(e.target.value)} />
          </div>
        </div>
        <p style={{
          fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
          margin: '10px 0 12px',
        }}>
          Leave the project list empty for every project you can access. Naming
          projects narrows the token — it can never widen your access, and a
          token narrowed this way cannot start a new project from the agent
          console (the new project would sit outside its own list).
        </p>
        <button
          onClick={() => void mint()}
          disabled={minting}
          style={{
            padding: '0.5rem 1.125rem', fontSize: '0.9375rem', fontWeight: 500,
            background: 'var(--color-accent)', color: '#fff',
            border: 'none', borderRadius: 'var(--radius-md)',
            cursor: minting ? 'not-allowed' : 'pointer',
            opacity: minting ? 0.6 : 1,
          }}
        >{minting ? 'Creating…' : 'Create token'}</button>
      </section>

      {/* ---- list ---- */}
      <section style={card}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: 12 }}>
          Your tokens
        </h3>
        {loading ? (
          <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
            Loading…
          </p>
        ) : tokens.length === 0 ? (
          <p style={{ fontSize: '0.9375rem', color: 'var(--color-text-secondary)' }}>
            No tokens yet.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                  <th style={{ padding: '6px 8px' }}>Label</th>
                  <th style={{ padding: '6px 8px' }}>Token</th>
                  <th style={{ padding: '6px 8px' }}>Scope</th>
                  <th style={{ padding: '6px 8px' }}>From</th>
                  <th style={{ padding: '6px 8px' }}>Last used</th>
                  <th style={{ padding: '6px 8px' }}>Expires</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} style={{
                    borderTop: '1px solid var(--color-border)',
                    opacity: t.revoked ? 0.5 : 1,
                  }}>
                    <td style={{ padding: '8px' }}>
                      {t.name || <em style={{ color: 'var(--color-text-secondary)' }}>unnamed</em>}
                      {t.revoked && (
                        <span style={{
                          marginLeft: 6, fontSize: '0.8125rem', padding: '1px 6px',
                          borderRadius: 10, background: 'var(--color-bg-primary)',
                          border: '1px solid var(--color-border)',
                        }}>revoked</span>
                      )}
                    </td>
                    <td style={{ padding: '8px', fontFamily: 'var(--font-mono, monospace)' }}>
                      {t.prefix}…
                    </td>
                    <td style={{ padding: '8px' }}>
                      {t.scope_kind === 'projects'
                        ? (() => {
                            try {
                              return (JSON.parse(t.project_ids || '[]') as string[]).join(', ')
                            } catch { return t.project_ids || '—' }
                          })()
                        : 'all my projects'}
                    </td>
                    <td style={{ padding: '8px' }}>{t.surface}</td>
                    <td style={{ padding: '8px' }}>
                      {fmt(t.last_used_at)}
                      {t.use_count > 0 && (
                        <span style={{ color: 'var(--color-text-secondary)' }}>
                          {' '}({t.use_count}×)
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px' }}>
                      {t.expires_at ? fmt(t.expires_at) : 'never'}
                    </td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>
                      {!t.revoked && (
                        <button
                          onClick={() => void revoke(t)}
                          style={{
                            padding: '0.25rem 0.625rem', fontSize: '0.8125rem',
                            background: 'transparent',
                            color: 'var(--color-danger, #d33)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 'var(--radius-md)', cursor: 'pointer',
                          }}
                        >Revoke</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  )
}
